import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import CreateMenu from "../components/ryweb/CreateMenu";
import { ANIM_STYLES } from "../components/ryweb/shared";
import { Dispatch, ShipmentDetail, AIQuote, Bidding, Documents, CustomerDetail, DriverDetail, Communications } from "../components/ryweb/ModulesA";
import { Invoices, Payouts, Margins, Claims, Roles, AuditLog } from "../components/ryweb/ModulesB";

const apple = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif";
const ORANGE = "#ff6b00";
const GREEN = "#10b981";
const BLUE = "#3b82f6";

/* ============================================================ */
/*  RY WEB · Mac desktop                                        */
/*  Portaled to document.body to escape iPhone bezel            */
/* ============================================================ */
export default function RYWebApp() {
  const [section, setSection] = useState("dashboard");
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const close = () => window.dispatchEvent(new CustomEvent("ry-web-close"));

  const time = now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const date = now.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });

  const overlay = (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "linear-gradient(135deg, #1a1830 0%, #2a1845 50%, #4a1f50 100%)", fontFamily: apple, color: "white", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      {/* Wallpaper layer */}
      <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(ellipse at 30% 20%, rgba(255,107,0,0.18), transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(59,130,246,0.18), transparent 50%)", pointerEvents: "none" }} />

      {/* macOS menu bar */}
      <div style={{ position: "relative", zIndex: 2, height: 28, background: "rgba(0,0,0,0.25)", backdropFilter: "blur(20px)", borderBottom: "0.5px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", padding: "0 14px", fontSize: 12, color: "white" }}>
        <button onClick={close} data-testid="mac-back" title="Back to iPhone"
          style={{ background: "none", border: "none", color: "white", cursor: "pointer", padding: "4px 8px 4px 4px", display: "flex", alignItems: "center", gap: 6, fontFamily: apple, fontSize: 12 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 6 9 12 15 18" /></svg>
          <span style={{ opacity: 0.8 }}>iPhone</span>
        </button>
        <div style={{ marginLeft: 14, display: "flex", alignItems: "center", gap: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <svg width="13" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.5 12.5c0-2 1-3 2.5-3.7-1-1.3-2.5-2-4.2-2-1.9 0-2.7 1-4 1-1.4 0-2.5-1-4-1-2.4 0-4.7 1.8-4.7 5.3 0 4 3.2 8.4 4.6 8.4 1.3 0 1.6-.9 3.3-.9 1.7 0 1.9.9 3.3.9 1.6 0 4.3-3.7 4.5-7.3a4 4 0 01-1.3-.7zM14.6 5.6c.9-1.1 1.6-2.6 1.4-4-1.3 0-2.7.8-3.5 1.8-.8 1-1.4 2.5-1.2 3.9 1.4.1 2.7-.7 3.3-1.7z" /></svg>
          </div>
          <span style={{ fontWeight: 700 }}>Safari</span>
          <span style={{ opacity: 0.7 }}>File</span>
          <span style={{ opacity: 0.7 }}>Edit</span>
          <span style={{ opacity: 0.7 }}>View</span>
          <span style={{ opacity: 0.7 }}>History</span>
          <span style={{ opacity: 0.7 }}>Bookmarks</span>
          <span style={{ opacity: 0.7 }}>Window</span>
          <span style={{ opacity: 0.7 }}>Help</span>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 14, fontSize: 11, opacity: 0.85 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12.55a11 11 0 0114.08 0" /><path d="M1.42 9a16 16 0 0121.16 0" /><path d="M8.53 16.11a6 6 0 016.95 0" /><circle cx="12" cy="20" r="0.7" fill="currentColor" /></svg>
          </span>
          <span>🔋</span>
          <span>{date}</span>
          <span style={{ fontWeight: 600 }}>{time}</span>
        </div>
      </div>

      {/* Window content */}
      <div style={{ position: "relative", zIndex: 1, flex: 1, padding: "26px 36px 60px", display: "flex", justifyContent: "center", overflow: "hidden" }}>
        <div style={{ width: "100%", maxWidth: 1280, background: "rgba(15,15,20,0.92)", backdropFilter: "blur(28px)", border: "0.5px solid rgba(255,255,255,0.12)", borderRadius: 14, boxShadow: "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,0,0,0.4)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Browser title bar with traffic lights */}
          <div style={{ height: 40, background: "linear-gradient(180deg, #2a2a2e, #1f1f23)", borderBottom: "0.5px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", padding: "0 14px", gap: 10 }}>
            <div style={{ display: "flex", gap: 7 }}>
              <button onClick={close} data-testid="mac-traffic-close" style={{ width: 12, height: 12, borderRadius: "50%", background: "#ff5f57", border: "0.5px solid rgba(0,0,0,0.2)", cursor: "pointer", padding: 0 }} />
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#febc2e", border: "0.5px solid rgba(0,0,0,0.2)" }} />
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#28c840", border: "0.5px solid rgba(0,0,0,0.2)" }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 12, opacity: 0.55 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6" /></svg>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
            </div>
            <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <div style={{ background: "rgba(255,255,255,0.06)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 7, padding: "5px 14px", display: "flex", alignItems: "center", gap: 8, minWidth: 340, maxWidth: 460, fontSize: 12 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 018 0v3" /></svg>
                <span style={{ color: "rgba(255,255,255,0.5)" }}>command.ryxpress.com</span>
                <span style={{ color: "white" }}>/dashboard</span>
              </div>
            </div>
            <div style={{ width: 100 }} />
          </div>

          {/* App layout: sidebar + content */}
          <div style={{ flex: 1, display: "flex", minHeight: 0, overflow: "hidden" }}>
            <style>{ANIM_STYLES}</style>
            <Sidebar section={section} setSection={setSection} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
              <TopBar />
              <div style={{ flex: 1, overflow: "auto", padding: 24 }}>
                {section === "dashboard" && <Dashboard setSection={setSection} />}
                {section === "dispatch" && <Dispatch />}
                {section === "shipments" && <ShipmentsListWithDetail />}
                {section === "aiquote" && <AIQuote />}
                {section === "bidding" && <Bidding />}
                {section === "documents" && <Documents />}
                {section === "customers" && <CustomersListWithDetail />}
                {section === "drivers" && <DriversListWithDetail />}
                {section === "communications" && <Communications />}
                {section === "invoices" && <Invoices />}
                {section === "payouts" && <Payouts />}
                {section === "margins" && <Margins />}
                {section === "claims" && <Claims />}
                {section === "roles" && <Roles />}
                {section === "audit" && <AuditLog />}
                {!["dashboard","dispatch","shipments","aiquote","bidding","documents","customers","drivers","communications","invoices","payouts","margins","claims","roles","audit"].includes(section) && <SectionView section={section} />}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mac Dock */}
      <Dock close={close} />
    </div>
  );
  return typeof document !== "undefined" ? createPortal(overlay, document.body) : overlay;
}

/* ---------- SIDEBAR (grouped) ---------- */
function Sidebar({ section, setSection }) {
  const groups = [
    {
      label: "OPERATIONS",
      items: [
        { id: "dashboard", label: "Dashboard", icon: <SvgHome /> },
        { id: "dispatch", label: "Dispatch", icon: <SvgTruck />, badge: 3 },
        { id: "shipments", label: "Shipments", icon: <SvgTruck /> },
        { id: "aiquote", label: "AI Quote Engine", icon: <SvgBolt /> },
        { id: "bidding", label: "Bidding", icon: <SvgGavel />, badge: 47 },
        { id: "documents", label: "Documents", icon: <SvgDoc /> },
      ],
    },
    {
      label: "CRM",
      items: [
        { id: "customers", label: "Customers", icon: <SvgUsers /> },
        { id: "drivers", label: "Drivers", icon: <SvgPerson /> },
        { id: "communications", label: "Inbox", icon: <SvgMsg />, badge: 12 },
      ],
    },
    {
      label: "FINANCE",
      items: [
        { id: "invoices", label: "Invoices", icon: <SvgDollar /> },
        { id: "payouts", label: "Payouts", icon: <SvgPayouts /> },
        { id: "margins", label: "Margins", icon: <SvgMargin /> },
        { id: "reports", label: "Reports", icon: <SvgChart /> },
      ],
    },
    {
      label: "COMPLIANCE",
      items: [
        { id: "claims", label: "Claims", icon: <SvgGavel />, badge: 9 },
        { id: "insurance", label: "Insurance", icon: <SvgShield /> },
        { id: "customs", label: "Customs", icon: <SvgCustoms /> },
      ],
    },
    {
      label: "PROFIT",
      items: [
        { id: "memberships", label: "RY+ Members", icon: <SvgStar /> },
        { id: "fuel", label: "Fuel Cards", icon: <SvgFuel /> },
      ],
    },
    {
      label: "ADMIN",
      items: [
        { id: "team", label: "Team", icon: <SvgTeam /> },
        { id: "roles", label: "Roles", icon: <SvgLock /> },
        { id: "audit", label: "Audit Log", icon: <SvgHistory /> },
        { id: "billing", label: "Billing", icon: <SvgCard /> },
        { id: "settings", label: "Settings", icon: <SvgGear /> },
      ],
    },
  ];
  return (
    <div style={{ width: 220, background: "rgba(0,0,0,0.25)", borderRight: "0.5px solid rgba(255,255,255,0.06)", padding: "14px 8px 14px", display: "flex", flexDirection: "column", gap: 6, overflowY: "auto" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, padding: "0 10px 8px" }}>
        <span style={{ fontSize: 22, fontWeight: 900, color: ORANGE, fontStyle: "italic", letterSpacing: -0.5 }}>RY</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: "white", letterSpacing: 2.5, fontStyle: "italic" }}>COMMAND</span>
      </div>
      {groups.map((g) => (
        <div key={g.label} style={{ marginBottom: 4 }}>
          <div style={{ fontSize: 9, letterSpacing: 1.4, color: "rgba(255,255,255,0.38)", fontWeight: 700, padding: "6px 10px 4px" }}>{g.label}</div>
          {g.items.map((it) => {
            const active = section === it.id;
            return (
              <button key={it.id} onClick={() => setSection(it.id)} data-testid={`web-nav-${it.id}`}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "6px 10px", borderRadius: 7, background: active ? `${ORANGE}1f` : "transparent", border: "none", color: active ? "white" : "rgba(255,255,255,0.65)", cursor: "pointer", fontFamily: apple, fontSize: 12.5, fontWeight: active ? 700 : 500, textAlign: "left", boxShadow: active ? `inset 0 0 0 0.5px ${ORANGE}55` : "none", marginBottom: 1 }}>
                <span style={{ color: active ? ORANGE : "rgba(255,255,255,0.55)", display: "flex", width: 14 }}>{it.icon}</span>
                <span style={{ flex: 1 }}>{it.label}</span>
                {it.badge && <span style={{ background: active ? ORANGE : "rgba(255,255,255,0.12)", color: active ? "white" : "rgba(255,255,255,0.65)", fontSize: 9.5, fontWeight: 800, padding: "1px 6px", borderRadius: 999 }}>{it.badge}</span>}
              </button>
            );
          })}
        </div>
      ))}
      <div style={{ marginTop: "auto", padding: 10, background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: 9 }}>
        <div style={{ fontSize: 9.5, letterSpacing: 1, color: "rgba(255,255,255,0.5)", fontWeight: 700 }}>PRO TIP</div>
        <div style={{ fontSize: 10.5, color: "white", marginTop: 3, lineHeight: 1.4 }}>Press <kbd style={{ background: "rgba(255,255,255,0.1)", padding: "1px 5px", borderRadius: 4, fontSize: 10 }}>⌘K</kbd> for command palette</div>
      </div>
    </div>
  );
}

/* ---------- TOP BAR ---------- */
function TopBar() {
  return (
    <div style={{ height: 56, borderBottom: "0.5px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", padding: "0 24px", gap: 16, background: "rgba(15,15,18,0.7)" }}>
      <div style={{ flex: 1, maxWidth: 520, background: "rgba(255,255,255,0.05)", border: "0.5px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "8px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="rgba(255,255,255,0.5)"><path d="M10 4a6 6 0 014.8 9.6l5.3 5.3-1.4 1.4-5.3-5.3A6 6 0 1110 4zm0 2a4 4 0 100 8 4 4 0 000-8z" /></svg>
        <span style={{ fontSize: 13, color: "rgba(255,255,255,0.5)" }}>Search shipments, drivers, invoices…</span>
        <span style={{ marginLeft: "auto", background: "rgba(255,255,255,0.06)", padding: "2px 7px", borderRadius: 5, fontSize: 10, color: "rgba(255,255,255,0.55)" }}>⌘ K</span>
      </div>
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 14 }}>
        <CreateMenu />
        <button style={{ background: "none", border: "none", color: "white", cursor: "pointer", position: "relative", padding: 4 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M18 16v-5a6 6 0 10-12 0v5l-2 2v1h16v-1l-2-2zM10 21a2 2 0 004 0" /></svg>
          <span style={{ position: "absolute", top: 2, right: 2, width: 7, height: 7, borderRadius: "50%", background: ORANGE, border: "1.5px solid #15151a" }} />
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&q=80" alt="Dwayne" style={{ width: 32, height: 32, borderRadius: "50%", border: `2px solid ${ORANGE}`, objectFit: "cover" }} />
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontSize: 12, fontWeight: 700 }}>Dwayne Johnson</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)" }}>RY Logistics</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- DASHBOARD ---------- */
function Dashboard({ setSection }) {
  const kpis = [
    { icon: <SvgTruck />, value: 128, label: "Total Shipments", delta: "12%" },
    { icon: <SvgClock />, value: 32, label: "In Transit", delta: "8%" },
    { icon: <SvgCheckCirc />, value: 45, label: "Delivered", delta: "25%" },
    { icon: <SvgDoc />, value: 18, label: "Pending", delta: "5%" },
    { icon: <SvgAlertTri />, value: 9, label: "Delayed", delta: "29%" },
    { icon: <SvgDollarC />, value: 84520, label: "Revenue This Week", delta: "15%", isMoney: true, hero: true },
  ];
  return (
    <div>
      {/* Animation keyframes */}
      <style>{`
        @keyframes ry-rise { from { transform: scaleY(0); opacity: 0; } to { transform: scaleY(1); opacity: 1; } }
        @keyframes ry-fade-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ry-draw { from { stroke-dashoffset: 600; } to { stroke-dashoffset: 0; } }
        @keyframes ry-pulse { 0% { box-shadow: 0 0 0 0 rgba(255,107,0,0.6); } 70% { box-shadow: 0 0 0 14px rgba(255,107,0,0); } 100% { box-shadow: 0 0 0 0 rgba(255,107,0,0); } }
        @keyframes ry-pulse-ring { 0% { transform: translate(-50%, -50%) scale(0.6); opacity: 0.7; } 100% { transform: translate(-50%, -50%) scale(2.4); opacity: 0; } }
        .ry-bar { transform-origin: bottom; animation: ry-rise 900ms cubic-bezier(.2,.7,.3,1) backwards; }
        .ry-card { animation: ry-fade-up 600ms cubic-bezier(.2,.7,.3,1) backwards; }
        .ry-line { stroke-dasharray: 600; animation: ry-draw 1200ms cubic-bezier(.2,.7,.3,1) backwards; animation-delay: 700ms; stroke-dashoffset: 600; animation-fill-mode: forwards; }
        .ry-line-dot { opacity: 0; animation: ry-fade-up 200ms forwards; }
        .ry-cmd-tile { transition: transform 180ms cubic-bezier(.2,.7,.3,1), background 180ms, border-color 180ms; }
        .ry-cmd-tile:hover { transform: translateY(-2px); background: rgba(255,107,0,0.07) !important; border-color: rgba(255,107,0,0.4) !important; }
        .ry-pin-ring { position: absolute; top: 50%; left: 50%; width: 18px; height: 18px; border-radius: 50%; background: ${ORANGE}; opacity: 0.5; animation: ry-pulse-ring 1.8s ease-out infinite; }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div className="ry-card">
          <div style={{ fontSize: 28, fontWeight: 800, color: "white", letterSpacing: -0.8 }}>Dashboard</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 3 }}>Here&apos;s what&apos;s happening with your operations today.</div>
        </div>
        <button style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.12)", color: "white", padding: "8px 12px", borderRadius: 9, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: apple, display: "flex", alignItems: "center", gap: 8 }}>This Week<span style={{ color: "rgba(255,255,255,0.5)", fontSize: 9 }}>▾</span></button>
      </div>

      {/* 6 KPI tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10, marginBottom: 18 }}>
        {kpis.map((k, i) => (
          <div key={k.label} className="ry-card" style={{ background: k.hero ? "linear-gradient(135deg, rgba(255,107,0,0.12), rgba(255,107,0,0.03))" : "rgba(255,255,255,0.025)", border: k.hero ? `1px solid ${ORANGE}66` : "0.5px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 14, position: "relative", overflow: "hidden", boxShadow: k.hero ? `0 0 24px ${ORANGE}22` : "none", animationDelay: `${i * 60}ms` }}>
            {k.hero && <div style={{ position: "absolute", top: -30, right: -30, width: 90, height: 90, borderRadius: "50%", background: `radial-gradient(circle, ${ORANGE}55, transparent 70%)`, pointerEvents: "none" }} />}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, position: "relative", zIndex: 1 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: `${ORANGE}1a`, border: `0.5px solid ${ORANGE}55`, display: "flex", alignItems: "center", justifyContent: "center", color: ORANGE, flexShrink: 0 }}>{k.icon}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: "white", letterSpacing: -0.5, lineHeight: 1, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                  <DashCountUp value={k.value} money={k.isMoney} />
                </div>
                <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.6)", marginTop: 3 }}>{k.label}</div>
              </div>
            </div>
            <div style={{ fontSize: 10, color: ORANGE, fontWeight: 700, marginTop: 10, position: "relative", zIndex: 1 }}>↗ {k.delta} <span style={{ color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>vs last week</span></div>
          </div>
        ))}
      </div>

      {/* Two-column main */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
        {/* LEFT COLUMN */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Operations Overview */}
          <Card title="Operations Overview" right={<PeriodPill />}>
            <Legend />
            <ComboChart />
          </Card>

          {/* Command Center */}
          <Card title="Command Center">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              {[
                { id: "shipments", icon: <SvgTruck />, title: "Shipments", sub: "Track and manage all shipments" },
                { id: "dispatch", icon: <SvgPin />, title: "Dispatch", sub: "Real-time location and status" },
                { id: "customers", icon: <SvgUsers />, title: "Customers", sub: "Manage your customers" },
                { id: "aiquote", icon: <SvgDoc />, title: "Quotes", sub: "Create and manage quotes" },
                { id: "reports", icon: <SvgChart />, title: "Reports", sub: "Analytics and performance" },
                { id: "drivers", icon: <SvgPerson />, title: "Drivers", sub: "Manage drivers and performance" },
                { id: "bidding", icon: <SvgGavel />, title: "Bidding", sub: "Reverse auction marketplace" },
                { id: "claims", icon: <SvgBell />, title: "Claims", sub: "Open incidents and risk" },
              ].map((t) => (
                <div key={t.title} onClick={() => setSection && setSection(t.id)} data-testid={`cc-tile-${t.id}`}
                  className="ry-cmd-tile" style={{ background: "rgba(255,255,255,0.025)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 11, padding: 12, cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: `${ORANGE}1a`, border: `0.5px solid ${ORANGE}55`, display: "flex", alignItems: "center", justifyContent: "center", color: ORANGE, flexShrink: 0 }}>{t.icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: "white", display: "flex", justifyContent: "space-between" }}>{t.title} <span style={{ color: "rgba(255,255,255,0.4)" }}>›</span></div>
                      <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.55)", marginTop: 3, lineHeight: 1.3 }}>{t.sub}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Recent Activity */}
          <Card title="Recent Activity" right={<ViewAll />}>
            {[
              { icon: <SvgLink />, title: "Shipment RY-78432 picked up in Toronto, ON", time: "May 19, 2025 · 9:15 AM", status: "In Transit", color: ORANGE },
              { icon: <SvgDoc />, title: "Invoice INV-2024-000158 paid by ABC Manufacturing", time: "May 18, 2025 · 4:32 PM", status: "Paid", color: GREEN },
              { icon: <SvgDoc />, title: "New quote QTE-2024-0052 created for Global Tech", time: "May 18, 2025 · 11:03 AM", status: "Quote Sent", color: "rgba(255,255,255,0.7)" },
              { icon: <SvgPerson />, title: "Shipment RY-78428 delivered in Calgary, AB", time: "May 16, 2025 · 11:32 AM", status: "Delivered", color: GREEN },
            ].map((a, i, arr) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: "12px 0", borderTop: i ? "0.5px solid rgba(255,255,255,0.06)" : "none", alignItems: "center" }}>
                <div style={{ width: 34, height: 34, borderRadius: "50%", background: `${ORANGE}1a`, border: `0.5px solid ${ORANGE}55`, color: ORANGE, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{a.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: "white" }}>{a.title}</div>
                  <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>{a.time}</div>
                </div>
                <span style={{ background: "transparent", border: `1px solid ${a.color}`, color: a.color, padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>{a.status}</span>
                {i + 1 === arr.length ? null : null}
              </div>
            ))}
          </Card>
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Live Shipments */}
          <Card title={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: GREEN, boxShadow: `0 0 8px ${GREEN}`, animation: "ry-pulse 1.6s infinite" }} />Live Shipments</span>} right={<ViewAll />}>
            <LiveMap />
            <div style={{ marginTop: 12 }}>
              {[
                { id: "RY-78432", lane: "Toronto, ON → Chicago, IL", eta: "May 20, 10:30 AM" },
                { id: "RY-78445", lane: "Calgary, AB → Dallas, TX", eta: "May 20, 2:15 PM" },
                { id: "RY-78451", lane: "Vancouver, BC → New York, NY", eta: "May 21, 9:45 AM" },
              ].map((s, i) => (
                <div key={s.id} style={{ display: "flex", gap: 10, padding: "10px 0", borderTop: i ? "0.5px solid rgba(255,255,255,0.06)" : "none", alignItems: "flex-start" }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: `${ORANGE}1a`, border: `0.5px solid ${ORANGE}55`, color: ORANGE, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><SvgTruck /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "white" }}>{s.id}</span>
                      <span style={{ fontSize: 10, color: ORANGE, fontWeight: 700 }}>In Transit</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 3 }}>
                      <span style={{ fontSize: 10.5, color: "rgba(255,255,255,0.7)" }}>{s.lane}</span>
                      <span style={{ fontSize: 10, color: "rgba(255,255,255,0.55)" }}>ETA: {s.eta}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Alerts & Issues */}
          <Card title="Alerts & Issues" right={<ViewAll />}>
            {[
              { title: "Shipment RY-78422 delayed in Memphis, TN", sub: "Weather conditions causing delay", time: "2h ago" },
              { title: "Driver availability low in Dallas, TX", sub: "Only 2 drivers available", time: "4h ago" },
              { title: "Maintenance due for Truck #1045", sub: "Scheduled maintenance overdue", time: "1d ago" },
            ].map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 10, padding: "10px 0", borderTop: i ? "0.5px solid rgba(255,255,255,0.06)" : "none", alignItems: "flex-start" }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: `${ORANGE}1a`, border: `0.5px solid ${ORANGE}55`, color: ORANGE, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><SvgAlertTri /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: "white" }}>{a.title}</div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>{a.sub}</div>
                </div>
                <span style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", whiteSpace: "nowrap", flexShrink: 0 }}>{a.time}</span>
              </div>
            ))}
          </Card>

          {/* Top Customers */}
          <Card title="Top Customers" right={<PeriodPill />}>
            {[
              { rank: 1, name: "ABC Manufacturing", count: "24 Shipments", amt: "$18,450" },
              { rank: 2, name: "Global Tech", count: "18 Shipments", amt: "$14,230" },
              { rank: 3, name: "Northwind Industries", count: "12 Shipments", amt: "$9,870" },
              { rank: 4, name: "Summit Retail Group", count: "9 Shipments", amt: "$7,240" },
              { rank: 5, name: "Metro Supply Co.", count: "7 Shipments", amt: "$5,410" },
            ].map((c, i) => (
              <div key={c.rank} style={{ display: "flex", gap: 10, padding: "9px 0", borderTop: i ? "0.5px solid rgba(255,255,255,0.06)" : "none", alignItems: "center" }}>
                <div style={{ width: 22, color: ORANGE, fontWeight: 800, fontSize: 12 }}>{c.rank}</div>
                <div style={{ flex: 1, fontSize: 12, fontWeight: 700, color: "white" }}>{c.name}</div>
                <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)" }}>{c.count}</div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "white", minWidth: 60, textAlign: "right" }}>{c.amt}</div>
              </div>
            ))}
            <button style={{ width: "100%", background: "transparent", border: "none", color: ORANGE, padding: "10px 0 4px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: apple }}>View All Customers</button>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ title, right, children }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.025)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "white" }}>{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}
function PeriodPill() { return <button style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.12)", color: "white", padding: "5px 10px", borderRadius: 7, fontSize: 10.5, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>This Week<span style={{ color: "rgba(255,255,255,0.5)", fontSize: 8 }}>▾</span></button>; }
function ViewAll() { return <button style={{ background: "none", border: "none", color: ORANGE, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>View All</button>; }
function Legend() {
  return (
    <div style={{ display: "flex", gap: 16, fontSize: 10.5, color: "rgba(255,255,255,0.7)", marginBottom: 10 }}>
      <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: ORANGE }} />Shipment Volume</span>
      <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 2, background: ORANGE }} />Revenue (USD)</span>
    </div>
  );
}
function ComboChart() {
  const days = [
    { label: "May 13", vol: 42, rev: 52 },
    { label: "May 14", vol: 38, rev: 50 },
    { label: "May 15", vol: 48, rev: 62 },
    { label: "May 16", vol: 68, rev: 83 },
    { label: "May 17", vol: 50, rev: 64 },
    { label: "May 18", vol: 58, rev: 73 },
    { label: "May 19", vol: 70, rev: 86 },
  ];
  const maxVol = 100;
  const maxRev = 100;
  const W = 100, H = 200, padX = 4;
  const x = (i) => padX + (i * (W - padX * 2)) / (days.length - 1);
  const yRev = (v) => H - (v / maxRev) * (H - 20) - 10;
  const linePts = days.map((d, i) => `${x(i)},${yRev(d.rev)}`).join(" ");
  return (
    <div style={{ position: "relative", display: "flex", gap: 0 }}>
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: H, fontSize: 9, color: "rgba(255,255,255,0.45)", paddingRight: 6, textAlign: "right" }}>
        {[100, 80, 60, 40, 20, 0].map((v) => <span key={v}>{v}</span>)}
      </div>
      <div style={{ flex: 1, position: "relative", height: H + 24 }}>
        <div style={{ position: "absolute", inset: `0 0 24px 0`, display: "flex", alignItems: "flex-end", justifyContent: "space-around", gap: 4 }}>
          {days.map((d, i) => (
            <div key={d.label} className="ry-bar" style={{ flex: 1, height: `${(d.vol / maxVol) * 100}%`, background: "linear-gradient(180deg, " + ORANGE + ", #b34800)", borderRadius: "6px 6px 0 0", boxShadow: `0 0 10px ${ORANGE}66`, maxWidth: 36, animationDelay: `${100 + i * 80}ms` }} />
          ))}
        </div>
        <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ position: "absolute", top: 0, left: 0, right: 0, pointerEvents: "none" }}>
          <polyline className="ry-line" points={linePts} stroke={ORANGE} strokeWidth="1.3" fill="none" vectorEffect="non-scaling-stroke" />
          {days.map((d, i) => (
            <circle key={i} className="ry-line-dot" style={{ animationDelay: `${1600 + i * 80}ms` }} cx={x(i)} cy={yRev(d.rev)} r="2" fill={ORANGE} />
          ))}
        </svg>
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, display: "flex", justifyContent: "space-around", fontSize: 9.5, color: "rgba(255,255,255,0.5)", paddingTop: 6 }}>
          {days.map((d) => <span key={d.label}>{d.label}</span>)}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: H, fontSize: 9, color: "rgba(255,255,255,0.45)", paddingLeft: 6 }}>
        {["$100K", "$80K", "$60K", "$40K", "$20K", "$0"].map((v) => <span key={v}>{v}</span>)}
      </div>
    </div>
  );
}
function DashCountUp({ value, money }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf, start = null;
    const dur = 1100;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(value * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  const rounded = Math.round(n);
  return <>{money ? `$${rounded.toLocaleString()}` : rounded.toLocaleString()}</>;
}
function LiveMap() {
  return (
    <div style={{ position: "relative", height: 150, borderRadius: 10, overflow: "hidden", background: "linear-gradient(135deg, #1a1a1d, #0e0e10)", border: "0.5px solid rgba(255,255,255,0.05)" }}>
      <div style={{ position: "absolute", inset: 0, opacity: 0.25, backgroundImage: `linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)`, backgroundSize: "22px 22px" }} />
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} viewBox="0 0 320 150" preserveAspectRatio="none">
        <path d="M30,80 Q 80,40 150,80 T 290,60" stroke={ORANGE} strokeWidth="2" fill="none" strokeDasharray="3 4" />
      </svg>
      {[
        { x: 8, y: 60 }, { x: 50, y: 60 }, { x: 92, y: 80 },
      ].map((p, i) => (
        <div key={i} style={{ position: "absolute", left: `${p.x}%`, top: `${p.y}%`, transform: "translate(-50%, -100%)" }}>
          <span className="ry-pin-ring" style={{ animationDelay: `${i * 600}ms` }} />
          <svg width="22" height="28" viewBox="0 0 24 30" style={{ position: "relative" }}>
            <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 18 12 18s12-9 12-18c0-6.6-5.4-12-12-12z" fill={ORANGE} stroke="#0a0a0a" strokeWidth="0.6" />
            <circle cx="12" cy="11" r="4" fill="white" />
          </svg>
        </div>
      ))}
    </div>
  );
}

/* Icons for dashboard */
function SvgClock() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>; }
function SvgCheckCirc() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="8 12 11 15 16 9" /></svg>; }
function SvgDoc() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>; }
function SvgAlertTri() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.86l-8.1 14a2 2 0 001.71 3h16.2a2 2 0 001.71-3l-8.1-14a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><circle cx="12" cy="17" r="0.7" fill="currentColor" /></svg>; }
function SvgDollarC() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M14.5 9.5A2.5 2.5 0 0012 8h-1a2 2 0 000 4h2a2 2 0 010 4h-1a2.5 2.5 0 01-2.5-1.5 M12 6v2M12 16v2" /></svg>; }
function SvgPin() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>; }
function SvgBell() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 16v-5a6 6 0 10-12 0v5l-2 2v1h16v-1l-2-2zM10 21a2 2 0 004 0" /></svg>; }
function SvgLink() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 007.07 0l3-3a5 5 0 00-7.07-7.07l-1.5 1.5" /><path d="M14 11a5 5 0 00-7.07 0l-3 3a5 5 0 007.07 7.07l1.5-1.5" /></svg>; }
/* ---------- KPI CARD ---------- */
function BigKpi({ label, value, delta, color, spark }) {
  const max = Math.max(...spark);
  const min = Math.min(...spark);
  const range = max - min || 1;
  const w = 100, h = 32;
  const pts = spark.map((v, i) => `${(i / (spark.length - 1)) * w},${h - ((v - min) / range) * (h - 4) - 2}`).join(" ");
  return (
    <div style={{ background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 16, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -30, right: -30, width: 100, height: 100, borderRadius: "50%", background: `radial-gradient(circle, ${color}22, transparent 70%)` }} />
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.8, marginTop: 4, position: "relative", zIndex: 1 }}>{value}</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 6 }}>
        <span style={{ fontSize: 11, color, fontWeight: 700 }}>↗ {delta}</span>
        <svg width={w} height={h}>
          <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  );
}

function BarChart() {
  const weeks = [2.4, 2.6, 2.5, 2.8, 3.0, 2.9, 3.1, 3.3, 3.2, 3.5, 3.7, 3.84];
  const max = Math.max(...weeks);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 160, padding: "20px 0 4px" }}>
      {weeks.map((v, i) => {
        const isLast = i === weeks.length - 1;
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>${v.toFixed(1)}M</div>
            <div style={{ width: "100%", height: `${(v / max) * 100}%`, background: isLast ? `linear-gradient(180deg, ${ORANGE}, #b34800)` : "linear-gradient(180deg, rgba(255,107,0,0.5), rgba(255,107,0,0.15))", borderRadius: "6px 6px 0 0", boxShadow: isLast ? `0 0 14px ${ORANGE}88` : "none", border: isLast ? `0.5px solid ${ORANGE}` : "0.5px solid rgba(255,107,0,0.3)" }} />
            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)" }}>W{i + 1}</div>
          </div>
        );
      })}
    </div>
  );
}

function SectionView({ section }) {
  const def = SECTIONS[section];
  if (!def) return <SectionPlaceholder section={section} />;
  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: 1.5, color: ORANGE, fontWeight: 700 }}>{def.kicker}</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: "white", letterSpacing: -0.8, marginTop: 4 }}>{def.title}</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 3 }}>{def.subtitle}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.12)", color: "white", padding: "7px 12px", borderRadius: 9, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: apple }}>Export CSV</button>
          <button style={{ background: ORANGE, border: "none", color: "white", padding: "7px 14px", borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: apple }}>{def.cta}</button>
        </div>
      </div>

      {/* KPIs */}
      {def.kpis && (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${def.kpis.length}, 1fr)`, gap: 12, marginBottom: 16 }}>
          {def.kpis.map((k) => (
            <div key={k.label} style={{ background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 14 }}>
              <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>{k.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "white", letterSpacing: -0.6, marginTop: 4 }}>{k.value}</div>
              {k.delta && <div style={{ fontSize: 10.5, color: k.deltaColor || GREEN, fontWeight: 700, marginTop: 3 }}>{k.delta}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Filter chips */}
      {def.chips && (
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          {def.chips.map((c, i) => (
            <button key={c} style={{ background: i === 0 ? `${ORANGE}1f` : "rgba(255,255,255,0.04)", border: `0.5px solid ${i === 0 ? ORANGE : "rgba(255,255,255,0.12)"}`, color: i === 0 ? ORANGE : "white", padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: apple }}>{c}</button>
          ))}
        </div>
      )}

      {/* Table */}
      <div style={{ background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 16 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ color: "rgba(255,255,255,0.55)", fontSize: 10, letterSpacing: 1, fontWeight: 700 }}>
              {def.columns.map((c) => (
                <th key={c.key} style={{ textAlign: c.align || "left", padding: "10px 6px", borderBottom: "0.5px solid rgba(255,255,255,0.08)" }}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {def.rows.map((row, i) => (
              <tr key={i} style={{ borderBottom: i === def.rows.length - 1 ? "none" : "0.5px solid rgba(255,255,255,0.04)" }}>
                {def.columns.map((col) => {
                  const val = row[col.key];
                  if (col.render === "status") {
                    return <td key={col.key} style={{ padding: "10px 6px" }}><span style={{ background: "transparent", border: `1px solid ${row.statusColor || ORANGE}`, color: row.statusColor || ORANGE, padding: "2px 9px", borderRadius: 999, fontSize: 10, fontWeight: 700 }}>{val}</span></td>;
                  }
                  if (col.render === "money") {
                    return <td key={col.key} style={{ padding: "10px 6px", textAlign: col.align || "left", fontWeight: 700 }}>{val}</td>;
                  }
                  if (col.render === "muted") {
                    return <td key={col.key} style={{ padding: "10px 6px", color: "rgba(255,255,255,0.7)" }}>{val}</td>;
                  }
                  return <td key={col.key} style={{ padding: "10px 6px", textAlign: col.align || "left", color: col.align === "right" ? "white" : "rgba(255,255,255,0.9)", fontWeight: col.bold ? 600 : 500 }}>{val}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const SECTIONS = {
  shipments: {
    kicker: "OPERATIONS", title: "Shipments", subtitle: "128 active · 32 in transit · $84K outstanding", cta: "+ New Shipment",
    kpis: [
      { label: "Active", value: "32", delta: "↗ +12% vs last week" },
      { label: "Delayed", value: "9", delta: "↗ +2", deltaColor: "#ef4444" },
      { label: "Unassigned", value: "14" },
      { label: "Revenue MTD", value: "$2.18M", delta: "↗ +18%" },
    ],
    chips: ["All · 128", "In Transit · 32", "Pending Pickup · 18", "Delivered · 45", "Delayed · 9"],
    columns: [
      { key: "id", label: "ID", bold: true },
      { key: "customer", label: "CUSTOMER" },
      { key: "lane", label: "LANE", render: "muted" },
      { key: "driver", label: "DRIVER", render: "muted" },
      { key: "eta", label: "ETA", render: "muted" },
      { key: "status", label: "STATUS", render: "status" },
      { key: "amt", label: "REVENUE", align: "right", render: "money" },
    ],
    rows: [
      { id: "RY-78432", customer: "ABC Manufacturing", lane: "Toronto → Chicago", driver: "D. Johnson", eta: "May 20 · 9 AM", status: "In Transit", statusColor: ORANGE, amt: "$4,250" },
      { id: "RY-78321", customer: "Global Tech", lane: "Atlanta → Dallas", driver: "D. Johnson", eta: "Delivered May 18", status: "Delivered", statusColor: GREEN, amt: "$2,875" },
      { id: "RY-78421", customer: "Acme Mfg.", lane: "Montreal → Toronto", driver: "D. Johnson", eta: "Today · 6 PM", status: "In Transit", statusColor: BLUE, amt: "$2,420" },
      { id: "RY-78211", customer: "Sunrise Dist.", lane: "LA → Phoenix", driver: "D. Johnson", eta: "Delayed · +45m", status: "Delayed", statusColor: "#ef4444", amt: "$3,960" },
      { id: "RY-78567", customer: "Retail Supply", lane: "Memphis → Nashville", driver: "Unassigned", eta: "May 20 · 4 PM", status: "Pending Pickup", statusColor: "#f59e0b", amt: "$1,125" },
      { id: "RY-78650", customer: "Northwest Goods", lane: "Seattle → Portland", driver: "Unassigned", eta: "May 21 · 2 PM", status: "Unassigned", statusColor: "rgba(255,255,255,0.6)", amt: "$950" },
    ],
  },
  drivers: {
    kicker: "FLEET", title: "Drivers", subtitle: "148 drivers · 1,247 RY+ members · 99.2% OTD", cta: "Invite Driver",
    kpis: [
      { label: "Active Today", value: "89" },
      { label: "RY+ Members", value: "1,247", delta: "↗ +18%" },
      { label: "Avg Rating", value: "4.89 ★" },
      { label: "OTD", value: "99.2%", delta: "↗ +0.4%" },
    ],
    chips: ["All · 148", "RY+ Gold · 312", "RY+ Silver · 645", "Basic · 290"],
    columns: [
      { key: "name", label: "DRIVER", bold: true },
      { key: "truck", label: "TRUCK", render: "muted" },
      { key: "miles", label: "MILES", render: "muted" },
      { key: "rating", label: "RATING" },
      { key: "tier", label: "TIER", render: "status" },
      { key: "earnings", label: "MTD EARNINGS", align: "right", render: "money" },
    ],
    rows: [
      { name: "Dwayne Johnson", truck: "T-204 · Kenworth", miles: "412K", rating: "4.96 ★", tier: "RY+ Gold", statusColor: "#f59e0b", earnings: "$9,820" },
      { name: "Dwayne Johnson", truck: "T-118 · Freightliner", miles: "380K", rating: "4.92 ★", tier: "RY+ Gold", statusColor: "#f59e0b", earnings: "$8,540" },
      { name: "Dwayne Johnson", truck: "T-307 · Volvo", miles: "122K", rating: "4.88 ★", tier: "RY+ Silver", statusColor: "rgba(255,255,255,0.7)", earnings: "$6,210" },
      { name: "Dwayne Johnson", truck: "T-411 · Peterbilt", miles: "450K", rating: "4.84 ★", tier: "Basic", statusColor: "rgba(255,255,255,0.5)", earnings: "$5,180" },
      { name: "Dwayne Johnson", truck: "T-602 · Kenworth", miles: "1.1M", rating: "4.91 ★", tier: "RY+ Gold", statusColor: "#f59e0b", earnings: "$10,420" },
    ],
  },
  customers: {
    kicker: "CRM", title: "Customers", subtitle: "62 active accounts · 12 prospects", cta: "+ Add Customer",
    kpis: [
      { label: "Active", value: "62" },
      { label: "Enterprise", value: "14" },
      { label: "Avg Margin", value: "23.4%", delta: "↗ +2.1pt" },
      { label: "ARR", value: "$5.84M", delta: "↗ +21%" },
    ],
    chips: ["All", "Enterprise · 14", "Mid · 26", "SMB · 22"],
    columns: [
      { key: "name", label: "CUSTOMER", bold: true },
      { key: "terms", label: "TERMS", render: "muted" },
      { key: "lanes", label: "LANES", render: "muted" },
      { key: "loads", label: "LOADS YTD", render: "muted" },
      { key: "margin", label: "MARGIN" },
      { key: "ytd", label: "YTD REVENUE", align: "right", render: "money" },
    ],
    rows: [
      { name: "ABC Manufacturing", terms: "Net 30", lanes: "24", loads: "188", margin: "26.2%", ytd: "$284K" },
      { name: "Global Tech Solutions", terms: "Net 15", lanes: "8", loads: "76", margin: "31.5%", ytd: "$142K" },
      { name: "Retail Supply Co.", terms: "Net 30", lanes: "11", loads: "94", margin: "22.8%", ytd: "$98K" },
      { name: "Sunrise Distributors", terms: "Net 7", lanes: "4", loads: "32", margin: "18.4%", ytd: "$54K" },
      { name: "Northwest Goods", terms: "Net 30", lanes: "6", loads: "28", margin: "21.0%", ytd: "$31K" },
    ],
  },
  insurance: {
    kicker: "PROFIT CENTER", title: "Cargo Insurance", subtitle: "326 policies · $284K MTD premiums · 31% loss ratio", cta: "Issue Policy",
    kpis: [
      { label: "Active Policies", value: "326" },
      { label: "MTD Premiums", value: "$284K", delta: "↗ +24%" },
      { label: "Open Claims", value: "3" },
      { label: "Loss Ratio", value: "31%", delta: "Industry avg 58%", deltaColor: GREEN },
    ],
    chips: ["All · 326", "Cargo · 248", "Liability · 54", "Cross-border · 24"],
    columns: [
      { key: "id", label: "POLICY ID", bold: true },
      { key: "customer", label: "CUSTOMER" },
      { key: "cover", label: "COVERAGE", render: "muted" },
      { key: "expires", label: "EXPIRES", render: "muted" },
      { key: "status", label: "STATUS", render: "status" },
      { key: "prem", label: "PREMIUM", align: "right", render: "money" },
    ],
    rows: [
      { id: "POL-3041", customer: "ABC Manufacturing", cover: "$100K cargo", expires: "Aug 2025", status: "Active", statusColor: GREEN, prem: "$1,250" },
      { id: "POL-3022", customer: "Global Tech", cover: "$250K cargo", expires: "Sep 2025", status: "Active", statusColor: GREEN, prem: "$2,100" },
      { id: "POL-3018", customer: "Sunrise Dist.", cover: "$50K reefer", expires: "May 31, 2025", status: "Renew", statusColor: ORANGE, prem: "$840" },
      { id: "POL-2987", customer: "Acme Mfg.", cover: "$500K liability", expires: "Dec 2025", status: "Active", statusColor: GREEN, prem: "$4,820" },
      { id: "POL-2901", customer: "Retail Supply", cover: "$100K cargo", expires: "Jul 2025", status: "Claim Open", statusColor: "#ef4444", prem: "$1,180" },
    ],
  },
  customs: {
    kicker: "CROSS-BORDER", title: "Customs & Brokerage", subtitle: "412 filings YTD · ACE (US) + ACI (CA) · $56.3K fees MTD", cta: "New Filing",
    kpis: [
      { label: "YTD Filings", value: "412" },
      { label: "Active ACE", value: "248" },
      { label: "Active ACI", value: "164" },
      { label: "MTD Fees", value: "$56.3K", delta: "↗ +9%" },
    ],
    chips: ["All · 412", "ACE (US) · 248", "ACI (CA) · 164", "Pending · 6"],
    columns: [
      { key: "id", label: "SHIPMENT", bold: true },
      { key: "type", label: "TYPE", render: "muted" },
      { key: "lane", label: "LANE", render: "muted" },
      { key: "crossing", label: "CROSSING", render: "muted" },
      { key: "status", label: "STATUS", render: "status" },
      { key: "fee", label: "FEE", align: "right", render: "money" },
    ],
    rows: [
      { id: "RY-78432", type: "ACE eManifest", lane: "Toronto → Chicago", crossing: "Detroit-Windsor", status: "Cleared", statusColor: GREEN, fee: "$135" },
      { id: "RY-78421", type: "ACI eManifest", lane: "Montreal → Toronto", crossing: "Lacolle", status: "Pending", statusColor: ORANGE, fee: "$135" },
      { id: "RY-78388", type: "ACE eManifest", lane: "Buffalo → Hamilton", crossing: "Peace Bridge", status: "Cleared", statusColor: GREEN, fee: "$135" },
      { id: "RY-78201", type: "ACI eManifest", lane: "Vancouver → Seattle", crossing: "Pacific Hwy", status: "Cleared", statusColor: GREEN, fee: "$135" },
      { id: "RY-78122", type: "ACE eManifest", lane: "Calgary → Great Falls", crossing: "Sweetgrass", status: "Hold", statusColor: "#ef4444", fee: "$135" },
    ],
  },
  memberships: {
    kicker: "PROFIT CENTER", title: "RY+ Memberships", subtitle: "1,247 active · $49/mo · $61.1K MRR · $733K ARR", cta: "Invite Driver",
    kpis: [
      { label: "Subscribers", value: "1,247", delta: "↗ +18%" },
      { label: "MRR", value: "$61.1K" },
      { label: "Churn", value: "1.8%", delta: "↓ -0.4pt", deltaColor: GREEN },
      { label: "LTV", value: "$2,460" },
    ],
    chips: ["All · 1,247", "Gold · 312", "Silver · 645", "Bronze · 290"],
    columns: [
      { key: "name", label: "DRIVER", bold: true },
      { key: "since", label: "MEMBER SINCE", render: "muted" },
      { key: "tier", label: "TIER", render: "status" },
      { key: "saved", label: "SAVINGS YTD", render: "muted" },
      { key: "next", label: "NEXT BILL", render: "muted" },
      { key: "amt", label: "MRR", align: "right", render: "money" },
    ],
    rows: [
      { name: "Dwayne Johnson", since: "Jan 2024", tier: "Gold", statusColor: "#f59e0b", saved: "$3,240", next: "Jun 1, 2025", amt: "$49" },
      { name: "Dwayne Johnson", since: "Mar 2024", tier: "Gold", statusColor: "#f59e0b", saved: "$2,810", next: "Jun 3, 2025", amt: "$49" },
      { name: "Dwayne Johnson", since: "Aug 2024", tier: "Silver", statusColor: "rgba(255,255,255,0.75)", saved: "$1,420", next: "Jun 8, 2025", amt: "$49" },
      { name: "Dwayne Johnson", since: "Feb 2024", tier: "Gold", statusColor: "#f59e0b", saved: "$3,720", next: "Jun 2, 2025", amt: "$49" },
      { name: "Dwayne Johnson", since: "Apr 2025", tier: "Bronze", statusColor: "rgba(255,255,255,0.5)", saved: "$240", next: "Jun 4, 2025", amt: "$49" },
    ],
  },
  fuel: {
    kicker: "PROFIT CENTER", title: "RY Fuel Card Network", subtitle: "892 active cards · 4% rebate · $42.8K rebate accrued MTD", cta: "Issue Card",
    kpis: [
      { label: "Active Cards", value: "892", delta: "↗ +37 this month" },
      { label: "Network Coverage", value: "98%" },
      { label: "MTD Rebate", value: "$42.8K", delta: "↗ +15%" },
      { label: "Fraud Blocks", value: "12" },
    ],
    chips: ["All · 892", "Active · 821", "Suspended · 71"],
    columns: [
      { key: "card", label: "CARD", bold: true },
      { key: "driver", label: "DRIVER", render: "muted" },
      { key: "limit", label: "MONTHLY LIMIT", render: "muted" },
      { key: "spent", label: "MTD SPEND", render: "muted" },
      { key: "rebate", label: "REBATE", render: "money" },
      { key: "status", label: "STATUS", render: "status" },
    ],
    rows: [
      { card: "•••• 4814", driver: "Dwayne Johnson", limit: "$5,000", spent: "$3,820", rebate: "$152", status: "Active", statusColor: GREEN },
      { card: "•••• 4815", driver: "Dwayne Johnson", limit: "$5,000", spent: "$2,940", rebate: "$117", status: "Active", statusColor: GREEN },
      { card: "•••• 4816", driver: "Dwayne Johnson", limit: "$3,500", spent: "$1,710", rebate: "$68", status: "Active", statusColor: GREEN },
      { card: "•••• 4817", driver: "Dwayne Johnson", limit: "$3,500", spent: "$2,180", rebate: "$87", status: "Active", statusColor: GREEN },
      { card: "•••• 4818", driver: "Dwayne Johnson", limit: "$3,500", spent: "$0", rebate: "$0", status: "Suspended", statusColor: "#ef4444" },
    ],
  },
  reports: {
    kicker: "ANALYTICS", title: "Reports", subtitle: "Revenue, performance, fleet & profitability", cta: "+ New Report",
    kpis: [
      { label: "Revenue MTD", value: "$3.84M", delta: "↗ +18%" },
      { label: "Gross Margin", value: "23.4%", delta: "↗ +2.1pt" },
      { label: "Avg Cost / Mi", value: "$1.82", delta: "↓ -$0.04", deltaColor: GREEN },
      { label: "Deadhead %", value: "8.2%", delta: "↓ -1.1pt", deltaColor: GREEN },
    ],
    chips: ["All", "Revenue", "Operations", "Fleet", "Finance"],
    columns: [
      { key: "name", label: "REPORT", bold: true },
      { key: "type", label: "TYPE", render: "muted" },
      { key: "owner", label: "OWNER", render: "muted" },
      { key: "freq", label: "FREQUENCY", render: "muted" },
      { key: "updated", label: "UPDATED", render: "muted" },
      { key: "value", label: "KEY METRIC", align: "right", render: "money" },
    ],
    rows: [
      { name: "Revenue by Lane", type: "Revenue", owner: "Dwayne Johnson", freq: "Daily", updated: "10 min ago", value: "$3.84M" },
      { name: "Driver Performance", type: "Operations", owner: "Dwayne Johnson", freq: "Weekly", updated: "Today", value: "99.2% OTD" },
      { name: "P&L Summary", type: "Finance", owner: "Dwayne Johnson", freq: "Monthly", updated: "Yesterday", value: "+24% margin" },
      { name: "Fleet Utilization", type: "Fleet", owner: "Dwayne Johnson", freq: "Daily", updated: "8 min ago", value: "82%" },
      { name: "Insurance Loss Ratio", type: "Finance", owner: "Dwayne Johnson", freq: "Monthly", updated: "May 1", value: "31%" },
      { name: "Customs ACE/ACI", type: "Operations", owner: "Dwayne Johnson", freq: "Weekly", updated: "Today", value: "412 filings" },
    ],
  },
  billing: {
    kicker: "PLATFORM", title: "Billing", subtitle: "RY Command · Enterprise plan · Next invoice Jun 1", cta: "Manage Plan",
    kpis: [
      { label: "Plan", value: "Enterprise" },
      { label: "MRR (paid)", value: "$999" },
      { label: "Outstanding", value: "$0" },
      { label: "Next Invoice", value: "Jun 1" },
    ],
    chips: ["All Invoices", "Paid · 12", "Pending · 0", "Refunded · 1"],
    columns: [
      { key: "id", label: "INVOICE", bold: true },
      { key: "date", label: "DATE", render: "muted" },
      { key: "plan", label: "PLAN", render: "muted" },
      { key: "method", label: "METHOD", render: "muted" },
      { key: "status", label: "STATUS", render: "status" },
      { key: "amt", label: "AMOUNT", align: "right", render: "money" },
    ],
    rows: [
      { id: "PI-2025-005", date: "May 1, 2025", plan: "Enterprise · monthly", method: "Visa •••• 4148", status: "Paid", statusColor: GREEN, amt: "$999.00" },
      { id: "PI-2025-004", date: "Apr 1, 2025", plan: "Enterprise · monthly", method: "Visa •••• 4148", status: "Paid", statusColor: GREEN, amt: "$999.00" },
      { id: "PI-2025-003", date: "Mar 1, 2025", plan: "Enterprise · monthly", method: "Visa •••• 4148", status: "Paid", statusColor: GREEN, amt: "$999.00" },
      { id: "PI-2025-002", date: "Feb 1, 2025", plan: "Enterprise · monthly", method: "Visa •••• 4148", status: "Paid", statusColor: GREEN, amt: "$999.00" },
      { id: "PI-2025-001", date: "Jan 1, 2025", plan: "Pro · monthly", method: "Visa •••• 4148", status: "Refunded", statusColor: "rgba(255,255,255,0.6)", amt: "$499.00" },
    ],
  },
  team: {
    kicker: "ACCOUNT", title: "Team & Permissions", subtitle: "5 members · 3 roles · last invite sent May 14", cta: "Invite Member",
    kpis: [
      { label: "Members", value: "5" },
      { label: "Admins", value: "2" },
      { label: "Pending Invites", value: "1" },
      { label: "MFA Enabled", value: "100%" },
    ],
    chips: ["All", "Owner · 1", "Admin · 1", "Manager · 2", "Viewer · 1"],
    columns: [
      { key: "name", label: "NAME", bold: true },
      { key: "email", label: "EMAIL", render: "muted" },
      { key: "role", label: "ROLE", render: "status" },
      { key: "last", label: "LAST ACTIVE", render: "muted" },
      { key: "mfa", label: "MFA", render: "muted" },
    ],
    rows: [
      { name: "Dwayne Johnson", email: "sarah@rylogistics.com", role: "Owner", statusColor: ORANGE, last: "Now", mfa: "TOTP" },
      { name: "Dwayne Johnson", email: "daniel@rylogistics.com", role: "Admin", statusColor: ORANGE, last: "12 min ago", mfa: "TOTP" },
      { name: "Dwayne Johnson", email: "mei@rylogistics.com", role: "Manager", statusColor: "white", last: "1 hr ago", mfa: "TOTP" },
      { name: "Dwayne Johnson", email: "joe@rylogistics.com", role: "Manager", statusColor: "white", last: "Yesterday", mfa: "TOTP" },
      { name: "Dwayne Johnson", email: "ana@rylogistics.com", role: "Viewer", statusColor: "rgba(255,255,255,0.6)", last: "3 days ago", mfa: "TOTP" },
    ],
  },
  settings: {
    kicker: "PLATFORM", title: "Settings", subtitle: "Region, locale, integrations and security", cta: "Save Changes",
    kpis: [
      { label: "Region", value: "North America" },
      { label: "Timezone", value: "EDT" },
      { label: "Currency", value: "CAD · $" },
      { label: "Integrations", value: "5/8 connected" },
    ],
    chips: ["All", "General", "Integrations", "Security", "Webhooks"],
    columns: [
      { key: "setting", label: "SETTING", bold: true },
      { key: "value", label: "CURRENT VALUE", render: "muted" },
      { key: "scope", label: "SCOPE", render: "muted" },
      { key: "edited", label: "LAST EDITED", render: "muted" },
      { key: "status", label: "STATUS", render: "status" },
    ],
    rows: [
      { setting: "Region", value: "North America (CA + US)", scope: "Org", edited: "May 1", status: "Active", statusColor: GREEN },
      { setting: "Timezone", value: "America/Toronto (EDT)", scope: "Org", edited: "Mar 12", status: "Active", statusColor: GREEN },
      { setting: "Currency", value: "CAD · auto-convert USD", scope: "Org", edited: "Mar 12", status: "Active", statusColor: GREEN },
      { setting: "QuickBooks", value: "Connected · sync nightly", scope: "Integration", edited: "Apr 22", status: "Connected", statusColor: GREEN },
      { setting: "Samsara ELD", value: "Connected · 142 trucks", scope: "Integration", edited: "Apr 22", status: "Connected", statusColor: GREEN },
      { setting: "Stripe Billing", value: "Connected", scope: "Integration", edited: "Mar 12", status: "Connected", statusColor: GREEN },
      { setting: "Twilio SMS", value: "Disconnected", scope: "Integration", edited: "—", status: "Connect", statusColor: ORANGE },
      { setting: "Webhooks", value: "8 endpoints · 0 failing", scope: "Platform", edited: "May 14", status: "Healthy", statusColor: GREEN },
    ],
  },
};

function SectionPlaceholder({ section }) {
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 40 }}>
      <div style={{ fontSize: 11, letterSpacing: 1.5, color: ORANGE, fontWeight: 700 }}>RY COMMAND</div>
      <div style={{ fontSize: 36, fontWeight: 800, letterSpacing: -1, marginTop: 8, textTransform: "capitalize" }}>{section}</div>
      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 6, maxWidth: 420 }}>Coming next.</div>
    </div>
  );
}

/* ---------- DOCK ---------- */
function Dock({ close }) {
  return (
    <div style={{ position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)", zIndex: 3, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(28px)", border: "0.5px solid rgba(255,255,255,0.18)", borderRadius: 18, padding: "8px 10px", display: "flex", gap: 6, boxShadow: "0 12px 30px rgba(0,0,0,0.4)" }}>
      <DockIcon label="Safari" color="linear-gradient(135deg, #4eb1ff, #1d6ed6)" emoji="🧭" active />
      <DockIcon label="Mail" color="linear-gradient(135deg, #6cd1ff, #1d8ad6)" emoji="✉" />
      <DockIcon label="Messages" color="linear-gradient(135deg, #5cf07c, #1aa632)" emoji="💬" />
      <DockIcon label="Calendar" color="linear-gradient(135deg, #ffffff, #e0e0e5)" emoji="📅" />
      <DockIcon label="Notes" color="linear-gradient(135deg, #ffe27a, #e0a015)" emoji="📝" />
      <DockIcon label="Finder" color="linear-gradient(135deg, #61c4ff, #2c5fbb)" emoji="🗂" />
      <DockIcon label="App Store" color="linear-gradient(135deg, #4eb1ff, #1d6ed6)" emoji="A" />
      <div style={{ width: 1, background: "rgba(255,255,255,0.2)", margin: "0 4px" }} />
      <DockIcon label="Back to iPhone" color={`linear-gradient(135deg, ${ORANGE}, #b34800)`} emoji="📱" onClick={close} testid="dock-back" />
    </div>
  );
}
function DockIcon({ label, color, emoji, active, onClick, testid }) {
  return (
    <button onClick={onClick} title={label} data-testid={testid}
      style={{ width: 44, height: 44, borderRadius: 11, background: color, border: "none", cursor: onClick ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, position: "relative", boxShadow: "0 4px 10px rgba(0,0,0,0.25)" }}>
      {emoji}
      {active && <span style={{ position: "absolute", bottom: -8, width: 4, height: 4, borderRadius: "50%", background: "white" }} />}
    </button>
  );
}

/* ---------- SVG ICONS ---------- */
function SvgHome() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l9-7 9 7v9a2 2 0 01-2 2h-4v-7h-6v7H5a2 2 0 01-2-2z" /></svg>; }
function SvgTruck() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h13l3 4v6H3z" /><circle cx="6.5" cy="17.5" r="1.6" /><circle cx="16.5" cy="17.5" r="1.6" /></svg>; }
function SvgPerson() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a8 8 0 0116 0v1" /></svg>; }
function SvgUsers() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="4" /><path d="M17 11a3 3 0 100-6" /><path d="M2 21v-2a5 5 0 015-5h4a5 5 0 015 5v2" /></svg>; }
function SvgShield() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L4 6v6c0 5 3.5 9.4 8 10 4.5-.6 8-5 8-10V6l-8-4z" /></svg>; }
function SvgCustoms() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="1" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>; }
function SvgStar() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15 9 22 9.5 17 14.5 18.5 22 12 18 5.5 22 7 14.5 2 9.5 9 9" /></svg>; }
function SvgFuel() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 22h12V4a2 2 0 00-2-2H5a2 2 0 00-2 2v18z" /><path d="M15 8h3a2 2 0 012 2v8a2 2 0 01-2 2" /></svg>; }
function SvgChart() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="20" x2="5" y2="13" /><line x1="12" y1="20" x2="12" y2="8" /><line x1="19" y1="20" x2="19" y2="16" /></svg>; }
function SvgCard() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>; }
function SvgTeam() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="4" /><path d="M2 21v-2a5 5 0 015-5h4a5 5 0 015 5v2" /></svg>; }
function SvgGear() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 008 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H2a2 2 0 110-4h.09A1.65 1.65 0 003.6 8a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H8a1.65 1.65 0 001-1.51V2a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V8a1.65 1.65 0 001.51 1H22a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" /></svg>; }

function SvgBolt() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10" /></svg>; }
function SvgGavel() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 14L4 24M14 6l4-4 6 6-4 4M9 11l4-4 4 4-4 4z" /></svg>; }
function SvgMsg() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11a8 8 0 01-12.7 6.5L3 19l1.5-5A8 8 0 1121 11z" /></svg>; }
function SvgDollar() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="22" /><path d="M17 6H9a3 3 0 000 6h6a3 3 0 010 6H7" /></svg>; }
function SvgPayouts() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1v22M5 8h11a3 3 0 010 6H8a3 3 0 000 6h11" /></svg>; }
function SvgMargin() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 8-8M14 7h7v7" /></svg>; }
function SvgLock() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>; }
function SvgHistory() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 109-9c-3 0-5.7 1.5-7.3 3.8M3 3v5h5M12 7v5l3 2" /></svg>; }

/* ---------- LIST + DETAIL WRAPPERS ---------- */
function ShipmentsListWithDetail() {
  const [view, setView] = useState("list");
  if (view === "detail") return (
    <div>
      <button onClick={() => setView("list")} data-testid="back-to-list"
        style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.1)", color: "white", padding: "6px 12px", borderRadius: 8, fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: apple, marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
        ← Back to Shipments
      </button>
      <ShipmentDetail />
    </div>
  );
  return <ClickableListSection section="shipments" onRowClick={() => setView("detail")} />;
}
function CustomersListWithDetail() {
  const [view, setView] = useState("list");
  if (view === "detail") return (
    <div>
      <button onClick={() => setView("list")} style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.1)", color: "white", padding: "6px 12px", borderRadius: 8, fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: apple, marginBottom: 14 }}>← Back to Customers</button>
      <CustomerDetail />
    </div>
  );
  return <ClickableListSection section="customers" onRowClick={() => setView("detail")} />;
}
function DriversListWithDetail() {
  const [view, setView] = useState("list");
  if (view === "detail") return (
    <div>
      <button onClick={() => setView("list")} style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.1)", color: "white", padding: "6px 12px", borderRadius: 8, fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: apple, marginBottom: 14 }}>← Back to Drivers</button>
      <DriverDetail />
    </div>
  );
  return <ClickableListSection section="drivers" onRowClick={() => setView("detail")} />;
}

/* Wrapper that re-uses the existing SectionView table but makes rows clickable */
function ClickableListSection({ section, onRowClick }) {
  const def = SECTIONS[section];
  if (!def) return <SectionPlaceholder section={section} />;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: 1.5, color: ORANGE, fontWeight: 700 }}>{def.kicker}</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: "white", letterSpacing: -0.8, marginTop: 4 }}>{def.title}</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 3 }}>{def.subtitle}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.12)", color: "white", padding: "7px 12px", borderRadius: 9, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: apple }}>Export CSV</button>
          <button style={{ background: ORANGE, border: "none", color: "white", padding: "7px 14px", borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: apple }}>{def.cta}</button>
        </div>
      </div>
      {def.kpis && (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${def.kpis.length}, 1fr)`, gap: 12, marginBottom: 16 }}>
          {def.kpis.map((k) => (
            <div key={k.label} style={{ background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 14 }}>
              <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>{k.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "white", letterSpacing: -0.6, marginTop: 4 }}>{k.value}</div>
              {k.delta && <div style={{ fontSize: 10.5, color: k.deltaColor || GREEN, fontWeight: 700, marginTop: 3 }}>{k.delta}</div>}
            </div>
          ))}
        </div>
      )}
      {def.chips && (
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          {def.chips.map((c, i) => (
            <button key={c} style={{ background: i === 0 ? `${ORANGE}1f` : "rgba(255,255,255,0.04)", border: `0.5px solid ${i === 0 ? ORANGE : "rgba(255,255,255,0.12)"}`, color: i === 0 ? ORANGE : "white", padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: apple }}>{c}</button>
          ))}
        </div>
      )}
      <div style={{ background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 16 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ color: "rgba(255,255,255,0.55)", fontSize: 10, letterSpacing: 1, fontWeight: 700 }}>
              {def.columns.map((c) => <th key={c.key} style={{ textAlign: c.align || "left", padding: "10px 6px", borderBottom: "0.5px solid rgba(255,255,255,0.08)" }}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {def.rows.map((row, i) => (
              <tr key={i} onClick={onRowClick} className="ry-clickable" data-testid={`row-${section}-${i}`}
                style={{ borderBottom: i === def.rows.length - 1 ? "none" : "0.5px solid rgba(255,255,255,0.04)", cursor: "pointer" }}>
                {def.columns.map((col) => {
                  const val = row[col.key];
                  if (col.render === "status") return <td key={col.key} style={{ padding: "10px 6px" }}><span style={{ background: "transparent", border: `1px solid ${row.statusColor || ORANGE}`, color: row.statusColor || ORANGE, padding: "2px 9px", borderRadius: 999, fontSize: 10, fontWeight: 700 }}>{val}</span></td>;
                  if (col.render === "money") return <td key={col.key} style={{ padding: "10px 6px", textAlign: col.align || "left", fontWeight: 700 }}>{val}</td>;
                  if (col.render === "muted") return <td key={col.key} style={{ padding: "10px 6px", color: "rgba(255,255,255,0.7)" }}>{val}</td>;
                  return <td key={col.key} style={{ padding: "10px 6px", textAlign: col.align || "left", color: col.align === "right" ? "white" : "rgba(255,255,255,0.9)", fontWeight: col.bold ? 600 : 500 }}>{val}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
